import { useState } from 'react';
import type { Company, CompanyInput } from '@provalia/shared';
import { ErrorBox, Modal, useToast } from '../../../shared/ui';
import { api, tenantPath } from '../../api';
import { CheckField, FormField } from '../../components/FormField';

export function CompanyModal({
  slug,
  company,
  isFirst,
  onClose,
  onSaved,
}: {
  slug: string;
  /** null = alta. */
  company: Company | null;
  isFirst: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
}): JSX.Element {
  const toast = useToast();
  const editing = company !== null;
  const [draft, setDraft] = useState<CompanyInput>(() => ({
    companyDb: company?.companyDb ?? '',
    name: company?.name ?? '',
    slUsername: company?.slUsername ?? '',
    slPassword: '',
    // Solo se precarga el CIF tecleado a mano: si viene de SAP, el campo queda vacío y SAP sigue mandando.
    cifOverride: company?.cifSource === 'panel' ? (company.cif ?? '') : '',
    isDefault: company?.isDefault ?? isFirst,
    active: company?.active ?? true,
  }));
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const set = <K extends keyof CompanyInput>(key: K, value: CompanyInput[K]): void => setDraft((d) => ({ ...d, [key]: value }));
  const missing = !draft.companyDb.trim() || !draft.name.trim() || !draft.slUsername.trim() || (!editing && !draft.slPassword);

  const save = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    const body: CompanyInput = { ...draft, companyDb: draft.companyDb.trim(), name: draft.name.trim(), slUsername: draft.slUsername.trim(), cifOverride: draft.cifOverride.trim() };
    try {
      if (editing) {
        await api.put(`${tenantPath(slug)}/companies/${encodeURIComponent(company.companyDb)}`, body);
        toast(`Sociedad ${body.companyDb} guardada`, 'ok');
      } else {
        await api.post(`${tenantPath(slug)}/companies`, body);
        toast(`Sociedad ${body.companyDb} añadida. Prueba la conexión.`, 'ok');
      }
      await onSaved();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <Modal
      title={editing ? `Editar ${company.companyDb}` : 'Añadir sociedad'}
      onClose={busy ? () => {} : onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button className="btn primary" onClick={() => void save()} disabled={busy || missing}>
            {busy ? 'Guardando…' : editing ? 'Guardar' : 'Añadir'}
          </button>
        </>
      }
    >
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          if (!missing) void save();
        }}
      >
        <div className="grid cols-2">
          <FormField label="Company DB" hint={editing ? 'No se puede cambiar.' : 'Nombre exacto de la base de datos de la sociedad en SAP.'}>
            <input
              className="input mono"
              value={draft.companyDb}
              readOnly={editing}
              spellCheck={false}
              placeholder="SBO_IMAR"
              onChange={(e) => set('companyDb', e.target.value)}
              autoFocus={!editing}
            />
          </FormField>
          <FormField label="Nombre" hint="Cómo se llama la sociedad en el portal.">
            <input className="input" value={draft.name} onChange={(e) => set('name', e.target.value)} />
          </FormField>
        </div>
        <div className="grid cols-2">
          <FormField label="Usuario del Service Layer">
            <input className="input mono" value={draft.slUsername} autoComplete="off" spellCheck={false} onChange={(e) => set('slUsername', e.target.value)} />
          </FormField>
          <FormField
            label="Contraseña"
            hint={editing ? 'Déjala vacía para conservar la guardada.' : 'Se guarda cifrada y nunca se vuelve a enseñar.'}
          >
            <input
              className="input"
              type="password"
              autoComplete="new-password"
              value={draft.slPassword}
              placeholder={editing && company.hasPassword ? '•••••••• (sin cambios)' : ''}
              onChange={(e) => set('slPassword', e.target.value)}
            />
          </FormField>
        </div>
        <FormField
          label="CIF tecleado a mano"
          hint="Normalmente vacío: el CIF se lee de la ficha de la sociedad en SAP. Escríbelo solo si SAP lo tiene mal; vaciarlo devuelve el mando a SAP."
        >
          <input
            className="input mono"
            value={draft.cifOverride}
            spellCheck={false}
            placeholder={company?.cifSource === 'sap' && company.cif ? `${company.cif} (de SAP)` : ''}
            onChange={(e) => set('cifOverride', e.target.value.toUpperCase())}
          />
        </FormField>
        <div className="row checks-row">
          <CheckField
            label="Sociedad principal"
            hint={isFirst ? 'La primera sociedad es siempre la principal.' : 'Con la que se habla cuando no se indica otra.'}
            checked={draft.isDefault}
            onChange={(v) => set('isDefault', v)}
          />
          <CheckField label="Activa" hint="Inactiva: el portal deja de usarla." checked={draft.active} onChange={(v) => set('active', v)} />
        </div>
        <ErrorBox error={error} />
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
